import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import ConfirmationDialog, {
  ConsequencesList,
} from '../../../components/ConfirmationDialog';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const VesselDeletionDialog = ({
  vesselId,
  onClose,
  onConfirm,
}: {
  vesselId: string | null;
  onClose: () => void;
  onConfirm: () => void;
}) => {
  const { deleteVessel } = useIotsApi();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const { t } = useLanguage();

  const deleteVesselMutation = useMutation({
    mutationFn: () => deleteVessel(vesselId || ''),
    onSuccess: () => {
      enqueueSnackbar({
        variant: 'success',
        message: t('content.vessel-deletion-success'),
      });
      onConfirm();
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(error, t('content.vessel-deletion-error')),
      });
      deleteVesselMutation.reset();
    },
  });

  return (
    <ConfirmationDialog
      open={Boolean(vesselId)}
      title={t('content.vessel-deletion-confirmation.title')}
      onConfirm={() => {
        if (vesselId) {
          deleteVesselMutation.mutate();
        }
      }}
      onClose={onClose}
      loading={deleteVesselMutation.isPending}
    >
      <ConsequencesList
        action={t('content.vessel-deletion-confirmation.action')}
        list={[
          t(
            'content.vessel-deletion-confirmation.bullet-points.will-be-visually-removed',
          ),
          t(
            'content.vessel-deletion-confirmation.bullet-points.submitted-journeys-unaffected',
          ),
          t(
            'content.vessel-deletion-confirmation.bullet-points.assigned-vessels-can-not-be-deleted',
          ),
        ]}
      />
    </ConfirmationDialog>
  );
};

export default VesselDeletionDialog;
